// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useEffect, useState } from 'react'
import { Sun, Moon, Menu, Maximize, Minimize } from 'lucide-react'
import { LogoWordmark } from '@qh/design-tokens/logos'
import { useTheme } from '@/context/theme-context'
import OrgSwitcher from '@/components/OrgSwitcher'

interface TopNavbarProps {
  onMobileMenuClick: () => void
}

export default function TopNavbar({ onMobileMenuClick }: TopNavbarProps) {
  const { theme, toggleTheme } = useTheme()
  const [isFullscreen, setIsFullscreen] = useState(false)

  useEffect(() => {
    const syncFullscreenState = () => setIsFullscreen(Boolean(document.fullscreenElement))

    syncFullscreenState()
    document.addEventListener('fullscreenchange', syncFullscreenState)
    return () => document.removeEventListener('fullscreenchange', syncFullscreenState)
  }, [])

  const toggleFullscreen = () => {
    const fullscreenAction = document.fullscreenElement
      ? document.exitFullscreen?.()
      : document.documentElement.requestFullscreen?.()

    void fullscreenAction?.catch(() => undefined)
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-14 bg-card border-b border-border flex items-center px-4 lg:px-6">
      <button
        onClick={onMobileMenuClick}
        className="lg:hidden mr-3 h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
        aria-label="Open navigation"
      >
        <Menu className="h-5 w-5" />
      </button>

      <div style={theme === 'dark' ? { filter: 'brightness(0) invert(1)' } : undefined}>
        <LogoWordmark className="h-7 w-auto" aria-hidden />
      </div>

      <div className="flex-1" />

      {/* Operator-only cross-org switcher (renders null for client staff). */}
      <div className="mr-2">
        <OrgSwitcher />
      </div>

      <div className="flex items-center gap-1">
        <button
          onClick={toggleFullscreen}
          className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
          {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
        </button>

        <button
          onClick={toggleTheme}
          className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
      </div>
    </header>
  )
}
